/**
 * FunnelNodeOverlay - Wrapper que adiciona métricas de funil sobre nodes originais
 * 
 * Envolve o node original com badges de leads, heatmap border,
 * barra de progresso e indicador de gargalo.
 * Não renderiza layout próprio - apenas sobrepõe métricas.
 * 
 * @release v2.1.0 - 2026-10-02 - Preserva as dimensões dos nodes de grupo.
 * @release v2.0.0 - 2026-03-07
 */

import { memo, type ComponentType } from 'react';
import { type NodeProps } from '@xyflow/react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FunnelNodeStats } from '@/hooks/useFlowFunnelStats';

/**
 * Determina a cor do heatmap baseado no percentual do funil
 * Verde: >70%, Amarelo: 30-70%, Vermelho: <30%
 */
function getHeatmapColor(percentage: number): string {
  if (percentage >= 70) return 'ring-green-500/60 shadow-green-500/20';
  if (percentage >= 30) return 'ring-yellow-500/60 shadow-yellow-500/20';
  return 'ring-red-500/60 shadow-red-500/20';
}

/**
 * Retorna cor do badge baseado na performance
 */
function getBadgeVariant(percentage: number): 'default' | 'secondary' | 'destructive' {
  if (percentage >= 70) return 'default';
  if (percentage >= 30) return 'secondary';
  return 'destructive';
}

/**
 * HOC que envolve um componente de node original com métricas de funil
 * Preserva o visual original e adiciona overlays de métricas por cima
 */
export function createFunnelWrapper(OriginalComponent: ComponentType<any>): ComponentType<NodeProps> {
  const FunnelWrapped = memo(function FunnelWrapped(props: NodeProps) {
    const stats: FunnelNodeStats | undefined = (props.data as any)?.funnelStats;
    const percentage = stats?.funnel_percentage ?? 0;
    const entries = stats?.entries ?? 0;
    const dropOffs = stats?.drop_offs ?? 0;
    const isBottleneck = dropOffs > 0 && entries > 0 && (dropOffs / entries) > 0.5;
    const avgTime = stats?.avg_time_seconds;
    // O grupo depende das dimensões persistidas pelo React Flow no elemento pai.
    const isGroupNode = props.type === 'group' || (props.data as any)?.type === 'group';

    return (
      <div
        className={cn(
          'relative cursor-pointer',
          isGroupNode && 'h-full w-full',
          /* Heatmap ring — borda colorida baseada no percentual do funil */
          stats && `ring-2 rounded-lg shadow-lg ${getHeatmapColor(percentage)}`,
        )}
      >
        {/* Indicador de gargalo no canto superior direito */}
        {isBottleneck && (
          <div className="absolute -top-2 -right-2 z-10">
            <div className="bg-destructive rounded-full p-1 shadow-md animate-pulse">
              <AlertTriangle className="h-3 w-3 text-destructive-foreground" />
            </div>
          </div>
        )}

        {/* Node original renderizado normalmente */}
        {/* Força selected=false para ocultar toolbar na view de analytics (somente leitura) */}
        <OriginalComponent {...props} selected={false} />
      </div>
    );
  });

  return FunnelWrapped;
}
